import { Extension } from '@tiptap/core';
import { Plugin, PluginKey, TextSelection } from '@tiptap/pm/state';
import { Decoration, DecorationSet } from '@tiptap/pm/view';

export const searchReplacePluginKey = new PluginKey('quillSearchReplace');

const MAX_RESULTS = 2000;

/**
 * Escape a string for use inside a RegExp.
 * @param {string} value - Raw text.
 * @returns {string} Escaped pattern.
 */
const escapeRegExp = (value) => value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

/**
 * Find every occurrence of `term` in the document, matching across mark boundaries inside
 * a textblock (each textblock is flattened to a string whose indices map 1:1 to positions).
 * @param {import('@tiptap/pm/model').Node} doc - Document.
 * @param {string} term - Search term.
 * @param {boolean} caseSensitive - Whether matching is case sensitive.
 * @returns {Array<{from: number, to: number}>} Match ranges.
 */
export function findMatches(doc, term, caseSensitive) {
  if (!term) return [];
  const results = [];
  const re = new RegExp(escapeRegExp(term), caseSensitive ? 'g' : 'gi');
  doc.descendants((node, pos) => {
    if (results.length >= MAX_RESULTS) return false;
    if (!node.isTextblock) return true;
    let text = '';
    node.forEach((child) => {
      text += child.isText ? child.text : '￼'.repeat(child.nodeSize);
    });
    const start = pos + 1;
    re.lastIndex = 0;
    let match = re.exec(text);
    while (match !== null && results.length < MAX_RESULTS) {
      if (match[0].length === 0) {
        re.lastIndex += 1;
      } else {
        results.push({ from: start + match.index, to: start + match.index + match[0].length });
      }
      match = re.exec(text);
    }
    return false;
  });
  return results;
}

/**
 * Build highlight decorations for the current results.
 * @param {import('@tiptap/pm/model').Node} doc - Document.
 * @param {Array<{from: number, to: number}>} results - Match ranges.
 * @param {number} index - Active result index.
 * @returns {DecorationSet} Decorations.
 */
function buildDecorations(doc, results, index) {
  if (!results.length) return DecorationSet.empty;
  return DecorationSet.create(
    doc,
    results.map((range, i) => Decoration.inline(range.from, range.to, {
      class: i === index ? 'q-search-match q-search-match-active' : 'q-search-match',
    }))
  );
}

/**
 * Compute the next plugin state.
 * @param {object} prev - Previous state.
 * @param {object} patch - Fields to change.
 * @param {import('@tiptap/pm/model').Node} doc - Current document.
 * @returns {object} Next state.
 */
function computeState(prev, patch, doc) {
  const next = { ...prev, ...patch };
  const results = findMatches(doc, next.searchTerm, next.caseSensitive);
  let index = typeof patch.index === 'number' ? patch.index : prev.index;
  if (!results.length) index = 0;
  else index = ((index % results.length) + results.length) % results.length;
  return { ...next, results, index, decorations: buildDecorations(doc, results, index) };
}

/**
 * Select the active match and scroll it into view without stealing focus from the find bar.
 * @param {import('@tiptap/pm/state').Transaction} tr - Transaction to extend.
 * @param {{from: number, to: number}|undefined} range - Range to select.
 * @returns {import('@tiptap/pm/state').Transaction} The transaction.
 */
function selectRange(tr, range) {
  if (!range) return tr;
  return tr.setSelection(TextSelection.create(tr.doc, range.from, range.to)).scrollIntoView();
}

/**
 * Decoration-based find & replace (Mod-F in the editor). State lives in a plugin; the
 * find bar reads `editor.storage.searchReplace` through useEditorState.
 */
const SearchReplace = Extension.create({
  name: 'searchReplace',

  addStorage() {
    return { searchTerm: '', replaceTerm: '', caseSensitive: false, results: [], index: 0 };
  },

  addCommands() {
    /**
     * Dispatch a state patch to the plugin.
     * @param {object} patch - Fields to change.
     * @param {boolean} [select] - Whether to select/scroll to the active match afterwards.
     * @returns {Function} TipTap command.
     */
    const patchCommand = (patch, select = false) => ({ tr, state, dispatch }) => {
      if (!dispatch) return true;
      tr.setMeta(searchReplacePluginKey, patch);
      tr.setMeta('addToHistory', false);
      if (select) {
        const prev = searchReplacePluginKey.getState(state);
        const next = computeState(prev, patch, state.doc);
        selectRange(tr, next.results[next.index]);
      }
      return true;
    };

    return {
      setSearchTerm: (searchTerm) => patchCommand({ searchTerm: searchTerm || '', index: 0 }),
      setReplaceTerm: (replaceTerm) => patchCommand({ replaceTerm: replaceTerm || '' }),
      setSearchCaseSensitive: (caseSensitive) => patchCommand({ caseSensitive: Boolean(caseSensitive) }),
      clearSearch: () => patchCommand({ searchTerm: '', replaceTerm: '', index: 0 }),
      nextSearchResult: () => ({ state, tr, dispatch }) => {
        const prev = searchReplacePluginKey.getState(state);
        return patchCommand({ index: prev.index + 1 }, true)({ state, tr, dispatch });
      },
      previousSearchResult: () => ({ state, tr, dispatch }) => {
        const prev = searchReplacePluginKey.getState(state);
        return patchCommand({ index: prev.index - 1 }, true)({ state, tr, dispatch });
      },
      replaceCurrentResult: () => ({ state, tr, dispatch }) => {
        const prev = searchReplacePluginKey.getState(state);
        const range = prev.results[prev.index];
        if (!range) return false;
        if (dispatch) {
          if (prev.replaceTerm) tr.insertText(prev.replaceTerm, range.from, range.to);
          else tr.delete(range.from, range.to);
        }
        return true;
      },
      replaceAllResults: () => ({ state, tr, dispatch }) => {
        const prev = searchReplacePluginKey.getState(state);
        if (!prev.results.length) return false;
        if (dispatch) {
          [...prev.results].reverse().forEach((range) => {
            if (prev.replaceTerm) tr.insertText(prev.replaceTerm, range.from, range.to);
            else tr.delete(range.from, range.to);
          });
        }
        return true;
      },
    };
  },

  addProseMirrorPlugins() {
    const storage = this.storage;
    return [
      new Plugin({
        key: searchReplacePluginKey,
        state: {
          init: () => ({
            searchTerm: '', replaceTerm: '', caseSensitive: false, results: [], index: 0, decorations: DecorationSet.empty,
          }),
          apply: (tr, prev) => {
            const patch = tr.getMeta(searchReplacePluginKey);
            let next = prev;
            if (patch) next = computeState(prev, patch, tr.doc);
            else if (tr.docChanged && prev.searchTerm) next = computeState(prev, {}, tr.doc);
            if (next !== prev) {
              storage.searchTerm = next.searchTerm;
              storage.replaceTerm = next.replaceTerm;
              storage.caseSensitive = next.caseSensitive;
              storage.results = next.results;
              storage.index = next.index;
            }
            return next;
          },
        },
        props: {
          decorations(state) {
            return searchReplacePluginKey.getState(state).decorations;
          },
        },
      }),
    ];
  },
});

export default SearchReplace;
